import type {
	InfiniteData,
	UseInfiniteQueryResult,
} from "@tanstack/react-query";
import { useMemo } from "react";
import type { ActivityOrTaskListItem } from "web_myplann/Activities/Helpers/isActivityListItem";
import type { DefaultListMetaDTO } from "web_myplann/Infrastructure/Api/Api";

type ListPage<TItem extends ActivityOrTaskListItem> = {
	data: TItem[];
	meta: DefaultListMetaDTO;
} | null;

export type ActivityGroup<
	TItem extends ActivityOrTaskListItem = ActivityOrTaskListItem,
> = {
	items: TItem[];
	totalCount: number;
	hasNextPage: boolean;
	isFetchingNextPage: boolean;
	fetchNextPage: () => void;
};

export const useActivityGroup = <TItem extends ActivityOrTaskListItem>(
	query: UseInfiniteQueryResult<InfiniteData<ListPage<TItem>>>,
): ActivityGroup<TItem> => {
	const { data, hasNextPage, isFetchingNextPage, fetchNextPage } = query;

	const pages = useMemo(() => data?.pages ?? [], [data]);

	// Keep the API's page order stable. Re-sorting all merged pages can move rows
	// the user has already seen when a later page is loaded.
	const items = useMemo(
		() => pages.flatMap((page) => page?.data ?? []),
		[pages],
	);

	return {
		items,
		totalCount: pages[0]?.meta?.totalCount ?? 0,
		hasNextPage,
		isFetchingNextPage,
		fetchNextPage,
	};
};
